/**
 * Campaign Updates Page - Progress news and updates
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, FileText, Calendar, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignUpdates = () => {
  const { slug } = useParams();

  const { data: campaign } = useQuery({
    queryKey: ['campaign', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}`);
      return res.json();
    }
  });

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ['campaign-updates', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/updates`);
      return res.json();
    }
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/campaigns/${slug}`}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileText className="h-8 w-8 text-emerald-600" />
            Progress Updates
          </h1>
          {campaign && (
            <p className="text-slate-500 mt-1">{campaign.name}</p>
          )}
        </div>

        {/* Updates List */}
        {isLoading ? (
          <div className="space-y-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 w-full" />)}
          </div>
        ) : updates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No updates posted yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {updates.map((update) => (
              <Card key={update.id} className="overflow-hidden">
                {update.image_url && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={update.image_url}
                      alt={update.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-2">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(update.created_at)}
                    </span>
                    {update.author && (
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {update.author}
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-xl">{update.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 whitespace-pre-wrap">{update.content}</p>
                  {update.tags && update.tags.length > 0 && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {update.tags.map((tag, i) => (
                        <Badge key={i} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignUpdates;
